import React, { createContext, useContext, useState, useEffect, ReactNode, Dispatch, SetStateAction } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../services/firebase';
import { userApi } from '../services/api/endpoints/user';

export interface ExtendedUser extends User {
  token_allowance?: number;
  tokens_left_current_period?: number;
  account_type?: string;
}
interface AuthContextType {
  currentUser: ExtendedUser | null;
  setCurrentUser: Dispatch<SetStateAction<ExtendedUser | null>>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  setCurrentUser: () => {},
  isLoading: true
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<ExtendedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const res = await userApi.getUser();

          const profileData = res.data;

          const extendedUser: ExtendedUser = {
            ...user,
            ...profileData
          };

          setCurrentUser(extendedUser);
        } catch (err) {
          console.error("Failed to load extended user data", err);
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
      }

      setIsLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    setCurrentUser,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};